import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AddIcon, CheckIcon, DeleteIcon, EditIcon, SearchIcon } from 'assets/icons';
import { ContactButton } from 'components/UI/Buttons';
import CloseableAlert from 'components/UI/CloseableAlert';
import DebtStatusBadge from 'components/UI/DebtStatusBadge';
import FeedbackSnackbar from 'components/UI/FeedbackSnackbar';
import PaperContainer from 'components/UI/PaperContainer';
import SearchBar from 'components/UI/SearchBar';
import TopicTag from 'components/UI/TopicTag';
import { type FC, useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const Section: FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <Box sx={{ mt: 6 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 0.5 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5), mb: 2 }}>
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

const Label: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: alpha(NAVY, 0.45),
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontSize: '10px',
      mt: 3,
      mb: 1,
      display: 'block',
    }}
  >
    {children}
  </Typography>
);

const Rule: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      bgcolor: alpha(YELLOW, 0.08),
      borderLeft: `3px solid ${YELLOW}`,
      px: 2,
      py: 1,
      borderRadius: `0 ${RADIUS_ITEM} ${RADIUS_ITEM} 0`,
      mb: 2,
    }}
  >
    <Typography variant="body2" sx={{ color: NAVY, fontWeight: 500, fontSize: '13px' }}>
      {children}
    </Typography>
  </Box>
);

const Swatch: FC<{ color: string; name: string; dark?: boolean }> = ({ color, name, dark }) => (
  <Box sx={{ textAlign: 'center', minWidth: 72 }}>
    <Box
      sx={{
        bgcolor: color,
        width: 72,
        height: 72,
        borderRadius: RADIUS_ITEM,
        border: dark ? 'none' : `1px solid ${alpha(NAVY, 0.08)}`,
      }}
    />
    <Typography variant="caption" sx={{ color: NAVY, fontWeight: 600, fontSize: '10px', mt: 0.5, display: 'block' }}>
      {name}
    </Typography>
    <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontSize: '9px', fontFamily: 'monospace' }}>
      {color}
    </Typography>
  </Box>
);

const CodeBlock: FC<{ children: string }> = ({ children }) => (
  <Box
    sx={{
      bgcolor: alpha(NAVY, 0.04),
      borderRadius: RADIUS_ITEM,
      p: 2,
      mt: 1,
      mb: 2,
      fontFamily: 'monospace',
      fontSize: '12px',
      color: NAVY,
      whiteSpace: 'pre',
      overflowX: 'auto',
    }}
  >
    {children}
  </Box>
);

/* ─── Main ───────────────────────────────────────────────────────────────── */

const DesignSystem: FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [snackSev, setSnackSev] = useState<'success' | 'error' | 'warning' | 'info' | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Box sx={{ bgcolor: SURFACE, minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5), mb: 3 }}>
          Guia prescriptiva para devs. Usa estos componentes y patrones para toda vista nueva.
        </Typography>

        {/* ═══════════════════════════════════════════════════════════════
            ESTRUCTURA DE UNA PAGINA NUEVA
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Estructura de una pagina"
          description="Toda vista nueva debe seguir este esqueleto. Se agrega la ruta en routes/routes.ts y el componente se crea en pages/NombrePagina/index.tsx."
        >
          <CodeBlock>{`// pages/MiPagina/index.tsx
import Container from '@mui/material/Container';
import PaperContainer from 'components/UI/PaperContainer';

const MiPagina = () => (
  <Container maxWidth="md" sx={{ pt: 2 }}>
    <PaperContainer title="Seccion" fullWidth>
      {/* contenido */}
    </PaperContainer>
  </Container>
);`}</CodeBlock>
          <Rule>
            No agregar titulo de pagina. El titulo lo provee el sidebar (nav item activo). Ir directo al contenido con
            PaperContainer. Container maxWidth md o lg segun densidad.
          </Rule>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            COLORES
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Colores"
          description="Solo estos colores. Nunca usar negro puro (#000) ni colores fuera de esta paleta."
        >
          <Rule>Texto siempre en NAVY. CTAs siempre en YELLOW. Importar todo desde styles/tokens.ts.</Rule>

          <Label>Marca (uso principal)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Swatch color={NAVY} name="Navy" dark />
            <Swatch color={YELLOW} name="Yellow" />
            <Swatch color="#FFFFFF" name="White" />
          </Box>

          <Label>Acentos (iconos, fondos suaves, ilustraciones)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Swatch color="#47BCD8" name="Cyan" dark />
            <Swatch color="#C2EBF4" name="Light Cyan" />
            <Swatch color={SURFACE} name="Surface" />
          </Box>

          <Label>Status (solo para estados, nunca decorativo)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Swatch color="#53B721" name="Success" dark />
            <Swatch color="#E7233B" name="Error" dark />
            <Swatch color="#CF5C09" name="Warning" dark />
            <Swatch color="#47BCD8" name="Info" dark />
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            SUPERFICIES
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Superficies" description="Nunca usar bordes para separar secciones. Solo tonos de fondo.">
          <Rule>
            Sin bordes 1px. Separar con cambio de tono de fondo. Unica excepcion: ghost border en glassmorphism (alpha
            0.06).
          </Rule>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: SURFACE,
                p: 3,
                borderRadius: RADIUS_CARD,
                minWidth: 170,
                border: `1px dashed ${alpha(NAVY, 0.1)}`,
              }}
            >
              <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontWeight: 600 }}>
                Fondo pagina
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: NAVY, mt: 0.5, fontSize: '11px' }}>
                SURFACE
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{ bgcolor: SURFACE_CONTAINER_LOW, p: 3, borderRadius: RADIUS_CARD, minWidth: 170 }}
            >
              <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontWeight: 600 }}>
                Sidebar / secciones
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: NAVY, mt: 0.5, fontSize: '11px' }}>
                SURFACE_CONTAINER_LOW
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                bgcolor: SURFACE_CONTAINER_LOWEST,
                p: 3,
                borderRadius: RADIUS_CARD,
                boxShadow: SHADOW,
                minWidth: 170,
              }}
            >
              <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontWeight: 600 }}>
                Cards / elementos
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: NAVY, mt: 0.5, fontSize: '11px' }}>
                SURFACE_CONTAINER_LOWEST
              </Typography>
            </Paper>

            <Box
              sx={{
                backdropFilter: GLASS_BLUR,
                bgcolor: GLASS_BACKGROUND,
                boxShadow: SHADOW,
                borderRadius: RADIUS_CARD,
                border: `1px solid ${alpha(NAVY, 0.06)}`,
                p: 3,
                minWidth: 170,
              }}
            >
              <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontWeight: 600 }}>
                Menus / dropdowns
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: NAVY, mt: 0.5, fontSize: '11px' }}>
                Glassmorphism
              </Typography>
            </Box>
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            TIPOGRAFIA
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Tipografia" description="Solo Jost. Siempre color NAVY para texto.">
          <Rule>
            Titulos: h4/h5 fontWeight 600. Cuerpo: body1/body2 Regular. Labels: caption, 600, uppercase, 10px. Texto
            secundario: alpha(NAVY, 0.6).
          </Rule>

          <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, p: 3, borderRadius: RADIUS_CARD, mt: 2 }}>
            <Typography variant="h4" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>
              Titulo de pagina (h4, 600)
            </Typography>
            <Typography variant="h5" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>
              Titulo de seccion (h5, 600)
            </Typography>
            <Typography variant="subtitle1" sx={{ color: NAVY, mb: 1 }}>
              Subtitulo (subtitle1)
            </Typography>
            <Typography variant="body1" sx={{ color: NAVY, mb: 1 }}>
              Texto principal. Usa body1 para parrafos y descripciones.
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6), mb: 1 }}>
              Texto secundario (body2, alpha 0.6). Metadata, ayuda, descripciones cortas.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha(NAVY, 0.45),
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '10px',
              }}
            >
              Label de grupo (caption, 600, uppercase)
            </Typography>
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            BOTONES
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Botones" description="Tres niveles de jerarquia. Siempre border-radius RADIUS_COIN (24px).">
          <Rule>
            CTA: ContactButton (amarillo). Secundario: outlined + RADIUS_COIN. Terciario: text. Nunca MUI sin redondear.
          </Rule>

          <Label>CTA principal (1 por vista, la accion mas importante)</Label>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ContactButton>Crear Lead</ContactButton>
            <ContactButton startIcon={<AddIcon />}>Agregar</ContactButton>
          </Box>

          <Label>Secundario (acciones complementarias)</Label>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" sx={{ borderRadius: RADIUS_COIN }} startIcon={<EditIcon />}>
              Editar
            </Button>
            <Button variant="outlined" color="error" sx={{ borderRadius: RADIUS_COIN }} startIcon={<DeleteIcon />}>
              Eliminar
            </Button>
          </Box>

          <Label>Terciario (navegacion, acciones menores)</Label>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="text">Ver detalle</Button>
            <Button variant="text" disabled>
              Deshabilitado
            </Button>
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            INPUTS
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Inputs" description="Busqueda: SearchBar. Formularios: TextField outlined size small.">
          <Label>Busqueda (SearchBar - incluye debounce)</Label>
          <SearchBar searchTerm={searchTerm} onSearchTermChange={setSearchTerm} />

          <Label>Campos de formulario (TextField outlined, size small)</Label>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <TextField label="Normal" size="small" variant="outlined" />
            <TextField label="Con error" size="small" variant="outlined" error helperText="Campo requerido" />
            <TextField label="Deshabilitado" size="small" variant="outlined" disabled />
          </Box>

          <Label>Layout de formulario recomendado</Label>
          <CodeBlock>{`<Stack spacing={2} sx={{ maxWidth: 480 }}>
  <TextField label="Nombre" size="small" />
  <TextField label="Email" size="small" />
  <TextField label="Mensaje" size="small" multiline rows={3} />
  <ContactButton>Enviar</ContactButton>
</Stack>`}</CodeBlock>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            STATUS & TAGS
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Status & Tags" description="Badges para estados. Tags para categorias.">
          <Rule>
            DebtStatusBadge para estados de entidad. TopicTag para categorias/etiquetas. No inventar badges custom.
          </Rule>

          <Label>DebtStatusBadge (estados)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <DebtStatusBadge text="Activo" color="success" />
            <DebtStatusBadge text="Pendiente" color="warning" />
            <DebtStatusBadge text="Rechazado" color="error" />
            <DebtStatusBadge text="Nuevo" color="info" />
          </Box>

          <Label>TopicTag (categorias)</Label>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TopicTag topicName="Seguros" color="#47BCD8" />
            <TopicTag topicName="Credito" color="#2C5889" />
            <TopicTag topicName="Eliminable" color="#E7233B" onDelete={() => {}} />
            <TopicTag topicName="Inactivo" inactive />
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            DIALOGS & MODALS
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Dialogs & Modals"
          description="AlertDialog para confirmaciones destructivas. Dialog simple para informacion. Nunca crear modals custom."
        >
          <Rule>
            Usar AlertDialog de components/UI para confirmar acciones. Para formularios en modal, usar Dialog de MUI con
            el patron de abajo.
          </Rule>

          <Label>AlertDialog (confirmar accion destructiva)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              sx={{ borderRadius: RADIUS_COIN }}
              onClick={() => setConfirmOpen(true)}
            >
              Eliminar (ver dialog)
            </Button>
            <Button variant="outlined" sx={{ borderRadius: RADIUS_COIN }} onClick={() => setAlertOpen(true)}>
              Info (ver dialog)
            </Button>
          </Box>

          {/* Confirm dialog */}
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: NAVY, fontWeight: 600 }}>
              Eliminar registro
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Esta accion no se puede deshacer. Se eliminara permanentemente el registro seleccionado.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              <Button onClick={() => setConfirmOpen(false)} color="error">
                Eliminar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Info dialog */}
          <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
            <DialogTitle sx={{ color: NAVY, fontWeight: 600 }}>Informacion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Este es un dialog informativo. Usa este patron para mostrar detalles o instrucciones al usuario.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAlertOpen(false)}>Entendido</Button>
            </DialogActions>
          </Dialog>

          <Label>Patron para dialog con formulario</Label>
          <CodeBlock>{`<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
  <DialogTitle sx={{ color: NAVY, fontWeight: 600 }}>
    Titulo del formulario
  </DialogTitle>
  <DialogContent>
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField label="Campo" size="small" fullWidth />
    </Stack>
  </DialogContent>
  <DialogActions>
    <Button onClick={onClose}>Cancelar</Button>
    <ContactButton onClick={handleSubmit}>Guardar</ContactButton>
  </DialogActions>
</Dialog>`}</CodeBlock>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            FEEDBACK
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Feedback" description="Snackbar para confirmaciones rapidas. Alert para mensajes persistentes.">
          <Rule>
            FeedbackSnackbar despues de guardar/eliminar/crear. CloseableAlert para avisos que necesitan atencion del
            usuario.
          </Rule>

          <Label>FeedbackSnackbar (click para probar)</Label>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(['success', 'error', 'warning', 'info'] as const).map((sev) => (
              <Button
                key={sev}
                variant="outlined"
                size="small"
                sx={{ borderRadius: RADIUS_COIN, textTransform: 'capitalize' }}
                onClick={() => setSnackSev(sev)}
              >
                {sev}
              </Button>
            ))}
          </Box>
          <FeedbackSnackbar
            open={snackSev !== null}
            onClose={() => setSnackSev(null)}
            message={`Operacion completada (${snackSev})`}
            severity={snackSev ?? 'success'}
          />

          <Label>CloseableAlert (avisos persistentes)</Label>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <CloseableAlert
              severity="info"
              msg="Tu integracion con WhatsApp esta pendiente de configuracion."
              title="Atencion"
            />
            <CloseableAlert severity="warning" msg="Tienes 3 leads sin asignar hace mas de 48 horas." />
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            CONTENEDORES
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Contenedores" description="PaperContainer para secciones. Paper para cards de metricas.">
          <Rule>
            Toda seccion de datos va en PaperContainer (fullWidth, con titulo). Cards de metricas usan Paper con hover
            lift.
          </Rule>

          <Label>PaperContainer (seccion con titulo y accion)</Label>
          <PaperContainer
            title="Mis Leads"
            actionBtn={
              <ContactButton startIcon={<AddIcon />} sx={{ height: 36 }}>
                Nuevo
              </ContactButton>
            }
            fullWidth
          >
            <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5) }}>
              Contenido de la seccion. Padding responsive: py 3, px 2 (mobile) / 4 (desktop).
            </Typography>
          </PaperContainer>

          <Label>Cards con hover lift (dashboards, metricas)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[
              { label: 'Leads nuevos', value: '142' },
              { label: 'Conversion', value: '23%' },
              { label: 'Respuesta', value: '4.2 min' },
            ].map(({ label, value }) => (
              <Paper
                key={label}
                sx={{
                  p: 3,
                  borderRadius: RADIUS_CARD,
                  flex: 1,
                  minWidth: 160,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(NAVY, 0.45),
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '10px',
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="h4" sx={{ color: NAVY, fontWeight: 700, mt: 1 }}>
                  {value}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            LOADING
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Loading States"
          description="LoadingOverlay para pantalla completa. CircularProgress inline para secciones."
        >
          <Rule>
            LoadingOverlay (components/UI/LoadingOverlay) para cargas de pagina. CircularProgress centrado para
            secciones parciales.
          </Rule>

          <Label>Loading inline (dentro de una seccion)</Label>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 120,
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
            }}
          >
            <CircularProgress size={40} sx={{ color: alpha(NAVY, 0.3) }} />
          </Box>

          <Label>Patron para carga condicional</Label>
          <CodeBlock>{`import LoadingOverlay from 'components/UI/LoadingOverlay';

// Pantalla completa (bloquea interaccion)
<LoadingOverlay visible={isLoading} />

// Inline (dentro de un contenedor)
{isLoading ? (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress size={40} sx={{ color: alpha(NAVY, 0.3) }} />
  </Box>
) : (
  <DataContent />
)}`}</CodeBlock>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            ANIMACIONES
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Animaciones & Transiciones"
          description="Durations y easings estandar. Nunca inventar timings custom."
        >
          <Rule>
            Hover: 0.2s ease-in-out. Navegacion/sidebar: 0.46s cubic-bezier(0.25, 0.8, 0.25, 1). Cards: translateY(-4px)
            + shadow bloom.
          </Rule>

          <Label>Hover lift (cards interactivas)</Label>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {['0.15s (fast)', '0.2s (standard)', '0.46s (signature)'].map((t) => (
              <Paper
                key={t}
                sx={{
                  p: 2,
                  borderRadius: RADIUS_CARD,
                  minWidth: 140,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: `transform ${t.split(' ')[0]}, box-shadow ${t.split(' ')[0]}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: NAVY, fontWeight: 600, fontSize: '12px' }}>
                  {t}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4) }}>
                  Hover me
                </Typography>
              </Paper>
            ))}
          </Box>

          <Label>Timings a usar</Label>
          <CodeBlock>{`// Botones, micro-interacciones
transition: 'all 0.15s ease-in-out'

// Inputs, cards, opacidad
transition: 'all 0.2s ease-in-out'

// Sidebar nav, transiciones principales
transition: 'all 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)'

// Card hover lift (siempre con shadow bloom)
transition: 'transform 0.2s, box-shadow 0.2s',
'&:hover': {
  transform: 'translateY(-4px)',
  boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
}`}</CodeBlock>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            BORDER RADIUS & SPACING
        ═══════════════════════════════════════════════════════════════ */}

        <Section title="Border Radius" description="Solo 3 valores. Nunca usar 4px o valores custom.">
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            {[
              { radius: RADIUS_COIN, label: 'RADIUS_COIN (24px)', desc: 'Botones, nav items' },
              { radius: RADIUS_CARD, label: 'RADIUS_CARD (16px)', desc: 'Cards, modals, menus' },
              { radius: RADIUS_ITEM, label: 'RADIUS_ITEM (8px)', desc: 'Chips, inputs, tooltips' },
            ].map(({ radius, label, desc }) => (
              <Box key={label} sx={{ textAlign: 'center' }}>
                <Box sx={{ bgcolor: alpha(NAVY, 0.06), width: 120, height: 64, borderRadius: radius }} />
                <Typography
                  variant="caption"
                  sx={{ color: NAVY, fontWeight: 600, fontSize: '11px', display: 'block', mt: 0.5 }}
                >
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontSize: '10px' }}>
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Section title="Spacing" description="Base 8px (MUI default). Usar sx={{ p: 2 }} = 16px.">
          <Rule>
            Card padding: p 3 (24px). Container: py 3, px {`{xs: 2, lg: 4}`}. Entre secciones: mt 6. Gaps: 1 a 2.
          </Rule>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', mt: 2 }}>
            {[
              { n: 1, use: 'Gaps inline' },
              { n: 2, use: 'Padding std' },
              { n: 3, use: 'Card padding' },
              { n: 4, use: 'Desktop px' },
            ].map(({ n, use }) => (
              <Box key={n} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{ bgcolor: alpha('#47BCD8', 0.25), width: n * 8, height: 40, borderRadius: '2px', mx: 'auto' }}
                />
                <Typography variant="caption" sx={{ fontSize: '10px', color: NAVY, fontWeight: 600 }}>
                  {n} = {n * 8}px
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '9px', color: alpha(NAVY, 0.4) }}>
                  {use}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            ICONOS
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Iconos"
          description="Importar desde assets/icons.tsx. Nunca directamente de @mui/icons-material."
        >
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            {[
              { Icon: AddIcon, name: 'AddIcon' },
              { Icon: EditIcon, name: 'EditIcon' },
              { Icon: DeleteIcon, name: 'DeleteIcon' },
              { Icon: SearchIcon, name: 'SearchIcon' },
              { Icon: CheckIcon, name: 'CheckIcon' },
            ].map(({ Icon, name }) => (
              <Box key={name} sx={{ textAlign: 'center' }}>
                <Icon sx={{ color: NAVY, fontSize: 28 }} />
                <Typography variant="caption" sx={{ display: 'block', fontSize: '9px', color: alpha(NAVY, 0.4) }}>
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
          <CodeBlock>{`// Correcto
import { EditIcon, DeleteIcon } from 'assets/icons';

// Incorrecto - nunca hacer esto
import Edit from '@mui/icons-material/Edit';`}</CodeBlock>
        </Section>

        {/* ═══════════════════════════════════════════════════════════════
            TOKENS REFERENCE
        ═══════════════════════════════════════════════════════════════ */}

        <Section
          title="Tokens"
          description="Todos los valores viven en styles/tokens.ts. Nunca hardcodear colores, sombras ni radios."
        >
          <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, p: 3, borderRadius: RADIUS_CARD, mt: 2, overflowX: 'auto' }}>
            {[
              { token: 'NAVY', value: NAVY },
              { token: 'YELLOW', value: YELLOW },
              { token: 'SURFACE', value: SURFACE },
              { token: 'SURFACE_CONTAINER_LOW', value: SURFACE_CONTAINER_LOW },
              { token: 'SURFACE_CONTAINER_LOWEST', value: SURFACE_CONTAINER_LOWEST },
              { token: 'SHADOW', value: '0px 12px 32px rgba(21,24,58,0.06)' },
              { token: 'GLASS_BLUR', value: GLASS_BLUR },
              { token: 'GLASS_BACKGROUND', value: GLASS_BACKGROUND },
              { token: 'RADIUS_COIN', value: RADIUS_COIN },
              { token: 'RADIUS_CARD', value: RADIUS_CARD },
              { token: 'RADIUS_ITEM', value: RADIUS_ITEM },
            ].map(({ token, value }) => (
              <Box
                key={token}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.75,
                  borderBottom: `1px solid ${alpha(NAVY, 0.06)}`,
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: NAVY, minWidth: 220, fontFamily: 'monospace', fontSize: '12px' }}
                >
                  {token}
                </Typography>
                <Typography sx={{ color: alpha(NAVY, 0.5), fontFamily: 'monospace', fontSize: '11px' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Divider sx={{ mt: 6, mb: 2, borderColor: alpha(NAVY, 0.06) }} />
        <Typography variant="caption" sx={{ color: alpha(NAVY, 0.35) }}>
          Fuente: .claude/skills/design/design-system.md | Tokens: styles/tokens.ts
        </Typography>
      </Container>
    </Box>
  );
};

export default DesignSystem;
